import { storedUserId, supabase } from '../supabase'
import { clearCache, readCache, writeCache } from '../../utils/localCache'
import { emailToUsername, identToEmail, looksLikeEmail, staffPassword } from '../credentials'
import { AuthError, type AuthBackend, type Session } from './types'
import { AUTH_ERRORS, errorFor } from './messages'
import type { Perms, Role } from '../../types'

/**
 * Signing in against Supabase.
 *
 * The username-and-PIN scheme carries across unchanged: the same synthetic
 * address and the same deterministic password as before, because the people
 * typing them should not have to learn anything new.
 *
 * One thing does get simpler. Firebase could not change another account's
 * password from a browser, so resetting a PIN meant creating a *new* account
 * with a suffixed address (`kasa+2@`, `kasa+3@`) and keeping an index of which
 * address was current. Supabase can update the account in place from an Edge
 * Function, so the suffix and the whole index disappear, the address is
 * always derivable from the username.
 */

const ROLES: Role[] = ['owner', 'manager', 'cashier', 'waiter', 'kitchen', 'kiosk']

function roleOf(value: unknown): Role {
  // Anything unrecognised lands on the least-privileged role. A session that
  // ends up able to take money because a field was misspelt is the wrong way
  // to fail.
  return ROLES.includes(value as Role) ? (value as Role) : 'waiter'
}

/**
 * Reads the membership record that decides what this account may do.
 *
 * Returns `null` when there is no record, that is, when the account has been
 * removed from the institution.
 */
async function readMembership(uid: string, tenantId: string) {
  if (!supabase) return null
  const { data, error } = await supabase
    .from('admins')
    .select('role, display_name, email, username, perms')
    .eq('id', uid)
    .eq('tenant_id', tenantId)
    .maybeSingle()

  if (error) throw error
  return data
}

async function readSuperadmin(): Promise<boolean> {
  if (!supabase) return false
  // A function rather than a table read: the superadmins table is closed to
  // everyone, and whether *you* are one is all any client needs to know.
  const { data, error } = await supabase.rpc('is_superadmin')
  if (error) {
    console.error('[auth] superadmin kontrolü başarısız', error)
    return false
  }
  return data === true
}

async function buildSession(
  uid: string,
  email: string,
  tenantId: string | null,
): Promise<Session | null> {
  const isSuperadmin = await readSuperadmin()

  // The system administrator belongs to no institution, so there is no
  // membership record to read, and demanding one would lock them out of the
  // very screens they need to appoint the first owner.
  if (isSuperadmin) {
    return {
      uid,
      email,
      username: emailToUsername(email),
      displayName: 'Sistem Yöneticisi',
      role: 'owner',
      isSuperadmin: true,
    }
  }

  if (!tenantId) return null

  const record = await readMembership(uid, tenantId)
  if (!record) return null

  return {
    uid,
    email,
    username: record.username || emailToUsername(email),
    displayName: record.display_name || undefined,
    role: roleOf(record.role),
    // Anything that is not an object reads as no permissions. The database
    // decides in the end, this copy only shapes what the till offers.
    perms:
      record.perms && typeof record.perms === 'object' && !Array.isArray(record.perms)
        ? (record.perms as Perms)
        : {},
    isSuperadmin: false,
  }
}

export const supabaseAuth: AuthBackend = {
  subscribe(tenantId, onSession) {
    if (!supabase) {
      onSession(null)
      return () => {}
    }

    let cancelled = false

    // Kayıtlı oturum kopyası (28 Eki 2026): yenileyince ve internetsizken
    // logoda beklemeden açılsın. Kim olduğu ve yetkileri yalnız ekranı
    // şekillendiriyor; veriye erişimi yine sunucu (RLS) belirliyor. Doğrulama
    // hemen arkadan geliyor, üyelik yoksa oturum kapanıyor.
    const scope = tenantId ?? '_'
    const fastUid = storedUserId()
    const cached = fastUid ? readCache<Session>(scope, `session.${fastUid}`) : null
    if (cached) onSession(cached)

    const resolve = async (uid: string | null, email: string) => {
      if (!uid) {
        if (fastUid) clearCache(scope, `session.${fastUid}`)
        if (!cancelled) onSession(null)
        return
      }
      try {
        const session = await buildSession(uid, email, tenantId)
        if (cancelled) return
        if (session) writeCache(scope, `session.${uid}`, session)
        else clearCache(scope, `session.${uid}`)

        if (!session) {
          // Şifre belirleme sayfasında (/sifre) oturum kapatılmıyor (24 Eki
          // 2026): tarayıcıda başka bir kurumun kodu kalmışsa üyelik
          // bulunamıyor, sıfırlama bağlantısının açtığı oturum kapanıyor ve
          // şifre "Auth session missing" diye kaydedilemiyordu. Şifre
          // kaydedilince ana sayfaya geçiliyor, üyelik orada yeniden aranıyor.
          if (window.location.pathname === '/sifre') {
            onSession(null)
            return
          }
          /**
           * Signed in, but no longer a member.
           *
           * Removing someone deletes their membership record and nothing else;
           * the login itself keeps working. Ending the session here puts the
           * real reason on the login screen, instead of leaving them on a till
           * where every query is refused and every screen blames the network.
           */
          console.warn('[auth] üyelik kaydı yok, oturum kapatılıyor')
          await supabase!.auth.signOut({ scope: 'local' })
          onSession(null)
          return
        }
        onSession(session)
      } catch (err) {
        /**
         * A failed read is not a removed account.
         *
         * Offline, the membership query fails the same way it would for
         * someone who had been deleted. Signing out on that would send a till
         * that lost signal back to a login screen it cannot use. So the
         * session is left alone and the error is only reported.
         */
        console.error('[auth] üyelik okunamadı', err)
        // Aynı kişinin kopyası ekrandaysa (çevrimdışı) o kalıyor.
        if (cached && cached.uid === uid) return
        if (!cancelled) onSession(null)
      }
    }

    // The session that is already stored, before any event arrives.
    void supabase.auth.getSession().then(({ data }) => {
      void resolve(data.session?.user?.id ?? null, data.session?.user?.email ?? '')
    })

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      void resolve(session?.user?.id ?? null, session?.user?.email ?? '')
    })

    return () => {
      cancelled = true
      sub.subscription.unsubscribe()
    }
  },

  /**
   * Sends a reset mail, for owners only.
   *
   * Staff addresses are synthetic and no mail would ever arrive at them, so
   * the screen offers this only once what was typed looks like an address.
   * The redirect lands on the same screen an invitation does, both end with
   * someone choosing a password against a session they already hold.
   */
  async sendPasswordReset(email) {
    if (!supabase) {
      const m = AUTH_ERRORS['GIRIS-6']
      throw new AuthError('not-configured', m.title, m.code)
    }

    const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
      redirectTo: `${window.location.origin}/sifre`,
    })
    if (error) throw new Error(error.message)
  },

  async signIn(tenantId, username, pin) {
    if (!supabase) {
      const m = AUTH_ERRORS['GIRIS-6']
      throw new AuthError('not-configured', m.title, m.code)
    }

    // Two kinds of account behind one field. An owner registered with their
    // own address and types it; staff were given a name and type only that,
    // never seeing the address built from it. What was typed decides which:
    // an "@" means the person is their own authority on the credential, so it
    // is passed through untouched along with the password they chose.
    const isEmail = looksLikeEmail(username)
    const email = identToEmail(username)
    const password = isEmail ? pin : staffPassword(username, pin)

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (error) {
      const text = (error.message ?? '').toLowerCase()

      // No route to the server. Distinguished carefully: telling someone
      // their PIN is wrong when the wifi is down sends them to change a
      // password that was never the problem.
      // Safari aynı hatayı "Load failed" diye yazıyor (28 Eki 2026): iPhone'da
      // internetsiz giriş "şifre hatalı" çıkıyordu. Tarayıcının çevrimdışı
      // bilgisi ve Supabase'in hata türü de sayılıyor.
      const kind = (error as { name?: string; status?: number })
      if (
        !navigator.onLine ||
        kind.name === 'AuthRetryableFetchError' ||
        kind.status === 0 ||
        text.includes('fetch') ||
        text.includes('network') ||
        text.includes('load failed') ||
        text.includes('internet')
      ) {
        const m = errorFor('offline')
        throw new AuthError('offline', m.title, m.code)
      }

      // Supabase throttles repeated attempts. Without naming this, the fourth
      // failed try looks identical to the first and people keep going.
      if (text.includes('rate') || (error as { status?: number }).status === 429) {
        const m = AUTH_ERRORS['GIRIS-4']
        throw new AuthError('rate-limited', m.title, m.code)
      }

      // Right password, unconfirmed address. Only reached with the correct
      // password, so naming it tells a stranger nothing.
      if (text.includes('not confirmed')) {
        const m = errorFor('not-confirmed')
        throw new AuthError('not-confirmed', m.title, m.code)
      }

      // Hesap silme talebiyle kilitlenmiş kurum (functions/account).
      if (text.includes('banned') || (error as { code?: string }).code === 'user_banned') {
        const m = errorFor('closed')
        throw new AuthError('closed', m.title, m.code)
      }

      // A wrong password and an unknown username report the same way, which
      // is what you want at a login screen: saying which was wrong tells an
      // attacker which usernames exist.
      const m = errorFor('bad-credentials')
      throw new AuthError('bad-credentials', m.title, m.code)
    }

    const uid = data.user?.id
    if (!uid) {
      const m = errorFor('unknown')
      throw new AuthError('unknown', m.title, m.code)
    }

    // The password proves who you are, not that you still belong here.
    const session = await buildSession(uid, data.user?.email ?? email, tenantId)
    if (!session) {
      await supabase.auth.signOut({ scope: 'local' })
      const m = errorFor('not-a-member')
      throw new AuthError('not-a-member', m.title, m.code)
    }
    return { isSuperadmin: session.isSuperadmin }
  },

  async signOut() {
    // Yalnızca bu oturum. Varsayılan ('global') hesabın bütün cihazlardaki
    // oturumlarını kapatıyordu: patron dizüstünden çıkınca telefonundan da
    // atılıyordu.
    await supabase?.auth.signOut({ scope: 'local' })
  },
}
